import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Disable add-to-cart and variant selectors if product requires a Lightbooth print but no printUrl is available
    if (this.#requiresPrintUrl() && !localStorage.getItem('printUrl')) {
      console.log('[Lightbooth] Product has print overlay but no printUrl in localStorage, disabling add to cart and variant selectors');
      this.refs.addToCartButtonContainer?.disable();
      this.#disableVariantSelectors();
    }
  }

  /**
   * Whether this product requires a printUrl (has the print_overlay metafield).
   * @returns {boolean}
   */
  #requiresPrintUrl() {
    return this.hasAttribute('data-has-print-overlay');
  }

  /**
   * Disables all variant selector inputs/buttons within the product section.
   */
  #disableVariantSelectors() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    const picker = section.querySelector('variant-picker');
    if (!picker) return;

    picker.querySelectorAll('input, button, select').forEach((el) => {
      el.disabled = true;
    });
    picker.style.opacity = '0.5';
    picker.style.pointerEvents = 'none';
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  #isLightColor(color) {
    let hex = color.trim().replace(/^#/, '');
    if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
    if (!/^[0-9a-f]{6}$/i.test(hex)) return false;
    const n = parseInt(hex, 16);
    const r = (n >> 16) & 0xff, g = (n >> 8) & 0xff, b = n & 0xff;
    // Perceived luminance (Rec. 709)
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) > 186;
  }

  #showLightboothError(message) {
    const dialog = document.createElement('dialog');
    dialog.className = 'dialog-modal lightbooth-preview-dialog color-scheme-1';

    const closeSvg = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2L2 12" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M12 12L2 2" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;

    dialog.innerHTML = `
      <button class="button button-unstyled close-button lightbooth-preview-dialog__close" aria-label="Close">${closeSvg}</button>
      <div class="lightbooth-preview-dialog__content">
        <h2 class="lightbooth-preview-dialog__heading">${window.__lightboothStrings?.errorTitle || 'Something went wrong'}</h2>
        <p class="lightbooth-preview-dialog__description">${message}</p>
        <div class="lightbooth-preview-dialog__actions">
          <button type="button" class="button lightbooth-preview-dialog__confirm">${window.__lightboothStrings?.reportOk || 'OK'}</button>
        </div>
      </div>
    `;

    const cleanup = () => {
      dialog.classList.add('dialog-closing');
      dialog.addEventListener('animationend', () => {
        dialog.close();
        dialog.remove();
      }, { once: true });
    };

    dialog.querySelector('.lightbooth-preview-dialog__close').addEventListener('click', cleanup);
    dialog.querySelector('.lightbooth-preview-dialog__confirm').addEventListener('click', cleanup);
    dialog.addEventListener('click', (e) => { if (e.target === dialog) cleanup(); });
    dialog.addEventListener('cancel', (e) => { e.preventDefault(); cleanup(); });

    document.body.appendChild(dialog);
    dialog.showModal();
  }

  /**
   * Fetches the composition for this product from the Lightbooth prints API.
   * The API returns a JSON object with two URLs — `preview` (downscaled, for
   * the in-dialog preview) and `full` (print-quality, attached to the cart).
   *
   * @returns {Promise<{ full: string, preview: string } | null>}
   */
  async #fetchComposition() {
    const shortId = localStorage.getItem('shortId');
    if (!shortId) {
      console.log('[Lightbooth] No shortId in localStorage, skipping');
      return null;
    }

    const productId = this.dataset.productId;
    if (!productId) {
      console.log('[Lightbooth] No productId on form, skipping');
      return null;
    }

    try {
      const apiUrl = new URL(`https://lbooth.com/prints/${shortId}/${productId}`);
      apiUrl.searchParams.set('design', '1');

      const response = await fetch(apiUrl.toString());
      if (!response.ok) {
        console.error('[Lightbooth] API returned status:', response.status);
        return null;
      }

      const result = await response.json();
      if (!result?.full?.startsWith('http') || !result?.preview?.startsWith('http')) {
        console.error('[Lightbooth] API returned invalid response (missing full/preview URLs):', result);
        return null;
      }

      return result;
    } catch (error) {
      console.error('[Lightbooth] API error:', error);
      return null;
    }
  }

  /**
   * Shows the Lightbooth print preview dialog. Runs in parallel with the
   * `cartAddPromise` (already in flight from #startCartAdd) — Checkout is
   * disabled until the POST resolves so by the time the user clicks Checkout
   * the cart is guaranteed populated.
   *
   * @param {string} imageUrl - The composition image URL to preview.
   * @param {Promise<object>} cartAddPromise - The in-flight /cart/add promise.
   * @param {FormData} formData - The submitted form data (used for CartAddEvent).
   * @returns {Promise<'cart' | null>} Resolves with 'cart' on Keep-browsing, null on cancel/problem. Checkout navigates away and never resolves.
   */
  #showPrintPreview(imageUrl, cartAddPromise, formData) {
    return new Promise((resolve) => {
      const dialog = document.createElement('dialog');
      dialog.className = 'dialog-modal lightbooth-preview-dialog color-scheme-1';

      // Apply product styling from print overlay config
      const bg = window.printOverlayConfig?.bg;
      if (bg) {
        dialog.style.setProperty('--lightbooth-bg', bg);
        if (this.#isLightColor(bg)) {
          dialog.dataset.bgTone = 'light';
        }
      }
      const radius = window.printOverlayConfig?.radius;
      if (radius) {
        dialog.style.setProperty('--lightbooth-radius', radius);
      }

      const closeSvg = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2L2 12" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M12 12L2 2" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;

      dialog.innerHTML = `
        <button class="button button-unstyled close-button lightbooth-preview-dialog__close" aria-label="Close">${closeSvg}</button>
        <div class="lightbooth-preview-dialog__content">
          <h2 class="lightbooth-preview-dialog__heading">${window.__lightboothStrings?.reviewTitle || 'Review your print'}</h2>
          <p class="lightbooth-preview-dialog__description">${window.__lightboothStrings?.reviewDescription || 'Please check that your print image looks correct before adding to cart.'}</p>
          <div class="lightbooth-preview-dialog__image-wrapper">
            <img alt="Your print design preview" class="lightbooth-preview-dialog__image" src="${imageUrl}" />
          </div>
          <div class="lightbooth-preview-dialog__actions">
            <button type="button" class="button button-secondary lightbooth-preview-dialog__keep-browsing">${window.__lightboothStrings?.addToCartKeepBrowsing || 'Keep browsing'}</button>
            <button type="button" class="button lightbooth-preview-dialog__checkout">${window.__lightboothStrings?.checkout || 'Checkout'}</button>
          </div>
          <button type="button" class="lightbooth-preview-dialog__cancel">${window.__lightboothStrings?.thereIsAProblem || 'There is a problem'}</button>
        </div>
      `;

      const cleanup = (result) => {
        dialog.classList.add('dialog-closing');
        dialog.addEventListener('animationend', () => {
          dialog.close();
          dialog.remove();
          resolve(result);
        }, { once: true });
      };

      // Track the cart-add result and the user's choice. Whichever happens
      // last (promise resolving vs. user clicking) performs the post-success
      // action via handlePostSuccess.
      let cartAddResolved = false;
      let cartAddResponse = null;
      let userChoice = null; // 'keep-browsing' | 'problem' | 'cancel'

      const checkoutBtn = dialog.querySelector('.lightbooth-preview-dialog__checkout');
      checkoutBtn.disabled = true;
      checkoutBtn.classList.add('is-loading');

      // Show the cart-add error in the existing addToCartTextError toast and
      // auto-close the dialog. The toast is theme-native (buy-buttons.liquid).
      const showError = (message) => {
        const { addToCartTextError } = this.refs;
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = message;
          } else {
            addToCartTextError.appendChild(document.createTextNode(message));
          }
        }
      };

      // After /cart/add succeeds, do whatever the user already decided:
      // dispatch CartAddEvent (Keep browsing → cart drawer opens), or silently
      // remove the line item (Problem / cancel).
      const handlePostSuccess = (response) => {
        if (userChoice === 'keep-browsing') {
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        } else if (userChoice === 'problem') {
          if (response.key) this.#removeFromCart(response.key);
        }
      };

      cartAddPromise.then((response) => {
        cartAddResolved = true;
        cartAddResponse = response;

        if (response?.error) {
          showError(window.__lightboothStrings?.networkError || 'Network error. Please try again.');
          cleanup(null);
          return;
        }
        if (response?.status) {
          showError(response.message);
          cleanup(null);
          return;
        }

        checkoutBtn.disabled = false;
        checkoutBtn.classList.remove('is-loading');
        handlePostSuccess(response);
      });

      // Close button / backdrop / ESC — dismiss the dialog but keep the item
      // in cart (same outcome as "Keep browsing"). Only "There is a problem"
      // explicitly removes the line item.
      const dismissHandler = () => {
        userChoice = 'keep-browsing';
        if (cartAddResolved && cartAddResponse && !cartAddResponse.error && !cartAddResponse.status) {
          handlePostSuccess(cartAddResponse);
        }
        cleanup(null);
      };

      dialog.querySelector('.lightbooth-preview-dialog__close').addEventListener('click', dismissHandler);
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog) dismissHandler();
      });
      dialog.addEventListener('cancel', (e) => {
        e.preventDefault();
        dismissHandler();
      });

      // "There is a problem" — fire alert, swap to report form, mark cancelled
      dialog.querySelector('.lightbooth-preview-dialog__cancel').addEventListener('click', () => {
        userChoice = 'problem';
        this.#sendProblemAlert(imageUrl);
        this.#showProblemReport(dialog, imageUrl);
        if (cartAddResolved && cartAddResponse && !cartAddResponse.error && !cartAddResponse.status) {
          handlePostSuccess(cartAddResponse);
        }
      });

      // Checkout — direct redirect, no cleanup. Button is only clickable once
      // /cart/add already succeeded, so the cart is guaranteed populated.
      checkoutBtn.addEventListener('click', () => {
        window.location.href = '/checkout';
      });

      // Keep browsing — close dialog; handlePostSuccess dispatches CartAddEvent
      // so the cart drawer opens. If the POST is still pending, the .then
      // handler above will dispatch when it resolves.
      dialog.querySelector('.lightbooth-preview-dialog__keep-browsing').addEventListener('click', (e) => {
        e.preventDefault();
        userChoice = 'keep-browsing';
        if (cartAddResolved && cartAddResponse && !cartAddResponse.error && !cartAddResponse.status) {
          handlePostSuccess(cartAddResponse);
        }
        cleanup('cart');
      });

      document.body.appendChild(dialog);
      dialog.showModal();

      // Wrapper starts square (CSS) — once the image's natural dimensions are
      // known, set aspect-ratio to match so the wrapper smoothly transitions
      // to the right shape instead of jumping when the image finishes loading.
      const wrapper = dialog.querySelector('.lightbooth-preview-dialog__image-wrapper');
      const imgEl = dialog.querySelector('.lightbooth-preview-dialog__image');
      imgEl.addEventListener('load', () => {
        if (imgEl.naturalWidth && imgEl.naturalHeight) {
          wrapper.style.aspectRatio = `${imgEl.naturalWidth} / ${imgEl.naturalHeight}`;
        }
      }, { once: true });
    });
  }

  /**
   * Fires an immediate problem report with all available context (no email yet).
   *
   * @param {string} designUrl - The design image URL.
   */
  #sendProblemAlert(designUrl) {
    const shortId = localStorage.getItem('shortId') || '';
    const printUrl = localStorage.getItem('printUrl') || '';
    const productTitle = document.querySelector('.view-product-title')?.textContent?.trim() || '';
    const variantId = this.refs.variantId?.value || '';

    fetch('https://lightbooth.dk/api/print-problem-report', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        shortId,
        printUrl,
        designUrl,
        productTitle,
        productUrl: window.location.href,
        variantId,
        timestamp: new Date().toISOString(),
      }),
    }).catch(() => {});
  }

  /**
   * Swaps the preview dialog content to a problem report form.
   * Collects the user's email and sends a report to support.
   *
   * @param {HTMLDialogElement} dialog - The existing dialog element.
   * @param {string} designUrl - The design image URL.
   */
  #showProblemReport(dialog, designUrl) {
    const content = dialog.querySelector('.lightbooth-preview-dialog__content');
    const s = window.__lightboothStrings || {};
    content.innerHTML = `
      <h2 class="lightbooth-preview-dialog__heading">${s.reportTitle || 'Report a problem'}</h2>
      <p class="lightbooth-preview-dialog__description">${s.reportDescription || 'Please enter your email so we can get back to you.'}</p>
      <form class="lightbooth-preview-dialog__report-form">
        <input
          type="email"
          class="lightbooth-preview-dialog__email-input"
          placeholder="${s.emailPlaceholder || 'Your email address'}"
          required
          autocomplete="email"
        />
        <button type="submit" class="button lightbooth-preview-dialog__send">${s.reportSend || 'Send'}</button>
      </form>
    `;

    const form = content.querySelector('.lightbooth-preview-dialog__report-form');
    const sendBtn = content.querySelector('.lightbooth-preview-dialog__send');
    const emailInput = content.querySelector('.lightbooth-preview-dialog__email-input');

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const email = emailInput.value.trim();
      if (!email) return;

      sendBtn.disabled = true;
      sendBtn.textContent = s.reportSending || 'Sending…';

      const shortId = localStorage.getItem('shortId') || '';
      const printUrl = localStorage.getItem('printUrl') || '';
      const productTitle = document.querySelector('.view-product-title')?.textContent?.trim() || '';
      const variantId = this.refs.variantId?.value || '';

      try {
        const res = await fetch('https://lightbooth.dk/api/print-problem-report', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            shortId,
            email,
            printUrl,
            designUrl,
            productTitle,
            productUrl: window.location.href,
            variantId,
            timestamp: new Date().toISOString(),
          }),
        });

        if (!res.ok) throw new Error('Request failed');

        content.innerHTML = `
          <h2 class="lightbooth-preview-dialog__heading">${s.reportSentTitle || 'Report sent'}</h2>
          <p class="lightbooth-preview-dialog__description">${s.reportSentDescription || 'Thank you! We\'ll get back to you shortly.'}</p>
          <button type="button" class="button lightbooth-preview-dialog__confirm">${s.reportOk || 'OK'}</button>
        `;
        content.querySelector('.lightbooth-preview-dialog__confirm').addEventListener('click', () => {
          dialog.classList.add('dialog-closing');
          dialog.addEventListener('animationend', () => {
            dialog.close();
            dialog.remove();
          }, { once: true });
        });
      } catch {
        sendBtn.disabled = false;
        sendBtn.textContent = s.reportSend || 'Send';
        content.insertAdjacentHTML('beforeend',
          `<p class="lightbooth-preview-dialog__error">${s.reportError || 'Something went wrong. Please try again.'}</p>`
        );
      }
    });
  }

  /**
   * POSTs to /cart/add.js without throwing. Resolves with the parsed JSON
   * (success: line item incl. `key`; server error: `{ status, message, … }`),
   * or `{ error }` on network failure.
   *
   * @param {FormData} formData - Form data including the `sections` payload.
   * @returns {Promise<object>}
   */
  async #startCartAdd(formData) {
    const fetchCfg = fetchConfig('javascript', { body: formData });
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });
      return await response.json();
    } catch (error) {
      console.error('[Lightbooth] /cart/add failed:', error);
      return { error };
    }
  }

  /**
   * Removes a line item from the cart by setting its quantity to 0.
   * Fire-and-forget — if it fails the user sees the item on next page load.
   *
   * @param {string} itemKey - The Shopify line item key.
   */
  async #removeFromCart(itemKey) {
    if (!itemKey) return;
    try {
      await fetch(`${Theme.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: itemKey, quantity: 0 }),
      });
    } catch (error) {
      console.error('[Lightbooth] Failed to remove cart item after cancel:', error);
    }
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
    event.preventDefault();
// TODO: disable the add to cart button and remember to re-enable it later
    if (this.#timeout) clearTimeout(this.#timeout);

    // Fetch the composition (Lightbooth-only — null for regular products).
    // Returns `{ full, preview }`: full goes on the line item, preview in the dialog.
    const composition = await this.#fetchComposition();

    // Block add to cart if this product requires a composition but we couldn't get one
    if (this.#requiresPrintUrl() && !composition) {
      console.error('[Lightbooth] Product requires a composition but failed to retrieve it, blocking add to cart');
      this.#sendProblemAlert('');
      this.#showLightboothError(window.__lightboothStrings?.errorNoPrint || 'Unable to load your print design. Please reload the page and try again.');
      return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    // Attach Lightbooth properties to the form before serializing.
    // The full URL is print-quality; only the preview is downscaled.
    if (composition) {
      const printInput = form.querySelector('[data-lightbooth-print-property]');
      if (printInput) {
        printInput.disabled = false;
        printInput.value = composition.full;
      }
      // Store the source moment URL as a hidden property (underscore prefix hides it from customer)
      const sourceInput = form.querySelector('[data-lightbooth-source-property]');
      const printUrl = localStorage.getItem('printUrl');
      if (sourceInput && printUrl) {
        sourceInput.disabled = false;
        sourceInput.value = printUrl;
      }
    }

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    // Lightbooth path: kick off /cart/add immediately (no await), show the
    // preview dialog in parallel. The dialog's Checkout button is gated on
    // the POST resolving, so by the time the user clicks Checkout the cart
    // is guaranteed populated and we can redirect instantly.
    if (composition) {
      const cartAddPromise = this.#startCartAdd(formData);
      await this.#showPrintPreview(composition.preview, cartAddPromise, formData);
      cartPerformance.measureFromEvent('add:user-action', event);
      return;
    }

    // Non-Lightbooth path: synchronous fetch (unchanged from theme baseline).
    const { addToCartTextError } = this.refs;
    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // [SCREEN READER] announce the error message via the live region
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // [SCREEN READER] clear the announcement after the toast hides
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            // [SCREEN READER] remove aria-live from the now-hidden error toast
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // [SCREEN READER] announce "Added" via the live region on successful add-to-cart
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  // [SCREEN READER] live-region helpers — write/clear text in the aria-live region
  // (`refs.liveRegion`) so assistive tech announces add-to-cart success and errors.

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
